import { useCallback, useRef, useState } from 'react';

import { getBeErrorMessage } from '~/common/utils/getBeErrorMessage';
import { logError, logWarn } from '~/common/utils/log';
import { AUTH_ERRORS, EMAIL_OTP_LENGTH } from '~/domains/auth/consts';
import type { AuthSuccessResponse } from '~/services/identity/unauthenticated/identity.service.api';
import { verifyOtp, resendOtp } from '~/services/identity/unauthenticated/identity.service.api';

import type { EmailVerificationStatus } from '../components/SignUpSteps/components/EmailVerificationForm';

function getErrorResponseData(
  error: unknown
): { error?: string; showBeMessage?: boolean; message?: string } | undefined {
  return (
    error as {
      response?: { data?: { error?: string; showBeMessage?: boolean; message?: string } };
    }
  )?.response?.data;
}

function useEmailVerification({
  onVerifySuccess,
  onResendError,
}: {
  onVerifySuccess: (data: AuthSuccessResponse) => void | Promise<void>;
  onResendError?: (beMessage?: string) => void;
}) {
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<EmailVerificationStatus>('idle');
  const [canResendIn, setCanResendIn] = useState<number>();
  const [email, setEmail] = useState<string>();
  const [code, setCode] = useState('');
  const [isTooManyAttempts, setIsTooManyAttempts] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const isVerifyingRef = useRef(false);

  const handleVerifyCode = useCallback(
    async (codeValue: string): Promise<boolean> => {
      if (!verificationToken) {
        logWarn('handleVerifyCode called without verificationToken');
        return false;
      }

      setVerificationStatus('loading');
      setErrorMessage(undefined);
      try {
        const data = await verifyOtp({ otp: codeValue, verificationToken });
        setVerificationStatus('success');
        await onVerifySuccess(data);
        return true;
      } catch (error: unknown) {
        const responseData = getErrorResponseData(error);
        if (responseData?.error === AUTH_ERRORS.TOO_MANY_ATTEMPTS) {
          setIsTooManyAttempts(true);
        } else {
          setVerificationStatus('error');
          const beMessage = getBeErrorMessage(error);
          if (beMessage) {
            setErrorMessage(beMessage);
          }
        }
        logError('OTP verification failed', error);
        return false;
      }
    },
    [verificationToken, onVerifySuccess]
  );

  const handleResendOtp = useCallback(async (): Promise<{
    success: boolean;
    beMessage?: string;
  }> => {
    if (!verificationToken) {
      logWarn('handleResendCode called without verificationToken');
      return { success: false };
    }

    try {
      const data = await resendOtp({ verificationToken });
      setCanResendIn(data.canResendIn);
      return { success: true };
    } catch (error: unknown) {
      const beMessage = getBeErrorMessage(error);
      logError('OTP resend failed', error);
      return { success: false, beMessage };
    }
  }, [verificationToken]);

  const submitCode = useCallback(
    async (codeValue: string) => {
      isVerifyingRef.current = true;
      try {
        const success = await handleVerifyCode(codeValue);
        if (!success) {
          setCode('');
        }
      } finally {
        isVerifyingRef.current = false;
      }
    },
    [handleVerifyCode]
  );

  // Verify on the last digit instead of waiting for a confirm step, matching iOS and Android
  const handleCodeChange = useCallback(
    (codeValue: string) => {
      setCode(codeValue);
      if (
        codeValue.length === EMAIL_OTP_LENGTH &&
        !isVerifyingRef.current &&
        verificationStatus !== 'success'
      ) {
        submitCode(codeValue);
      }
    },
    [submitCode, verificationStatus]
  );

  const handleResend = useCallback(async () => {
    const { success, beMessage } = await handleResendOtp();
    if (success) {
      setVerificationStatus('idle');
      setCode('');
    } else {
      onResendError?.(beMessage);
    }
  }, [handleResendOtp, onResendError]);

  const initMfa = useCallback((token: string, mfaEmail: string, canResendInValue?: number) => {
    setVerificationToken(token);
    setEmail(mfaEmail);
    setCode('');
    setVerificationStatus('idle');
    setCanResendIn(canResendInValue);
    setIsTooManyAttempts(false);
    setErrorMessage(undefined);
  }, []);

  const resetVerification = useCallback(() => {
    setVerificationStatus('idle');
    setCode('');
    setEmail(undefined);
    setVerificationToken(null);
    setCanResendIn(undefined);
    setIsTooManyAttempts(false);
    setErrorMessage(undefined);
  }, []);

  return {
    verificationStatus,
    verificationErrorMessage: errorMessage,
    canResendIn,
    email,
    code,
    handleCodeChange,
    isTooManyAttempts,
    handleResend,
    initMfa,
    resetVerification,
  };
}

export default useEmailVerification;
